import {
  formatSolanaTokenAmount,
  parseSolanaTokenAmount,
  type SolanaNetworkConfig,
  type SolanaTokenInfo,
} from "../../config/solana";

export function solanaAmount(
  value: string,
  token: SolanaTokenInfo,
): bigint | null {
  try {
    const amount = parseSolanaTokenAmount(value, token.decimals);
    return amount > 0n && amount < 1n << 64n ? amount : null;
  } catch {
    return null;
  }
}

export function solanaSendAmounts(
  _network: SolanaNetworkConfig,
  token: SolanaTokenInfo,
  inputs: string[],
  balance: bigint,
  kind: "private" | "wallet" | "batch" | "invalid",
  quotedFee: bigint | null = null,
) {
  const parsed = inputs.map((input) => solanaAmount(input, token));
  const amount = parsed.reduce<bigint>((sum, value) => sum + (value ?? 0n), 0n);
  const needsQuote = kind === "wallet" || kind === "batch";
  const fee = needsQuote ? (quotedFee ?? 0n) : 0n;
  const total = kind === "batch" ? amount + fee : amount;
  const received =
    kind === "wallet" ? (amount > fee ? amount - fee : 0n) : amount;
  const error =
    !inputs.length ||
    (kind === "batch" && inputs.length < 2) ||
    inputs.length > 32 ||
    total >= 1n << 64n ||
    parsed.some((value) => value === null)
      ? "Enter a valid amount for each recipient."
      : total > balance
        ? `Insufficient private balance. Available: ${formatSolanaTokenAmount(balance, token.decimals)} ${token.symbol}. Please deposit more funds.`
        : needsQuote && (quotedFee === null || quotedFee < 0n)
          ? "Waiting for a live withdrawal fee quote."
          : kind === "batch" && fee > amount
            ? "Recipient total must cover the relayer fee."
            : kind === "wallet" && amount <= fee
              ? `Amount must exceed the ${formatSolanaTokenAmount(fee, token.decimals)} ${token.symbol} relayer fee.`
              : kind === "invalid"
                ? "Enter a private address or a Solana wallet address."
                : null;
  return { amount, fee, total, received, error };
}

/** A fresh no-change quote supplies the total net amount available to a batch. */
export function solanaBatchMax(
  token: SolanaTokenInfo,
  maxTotal: bigint,
  otherAmounts: string[],
): bigint {
  let others = 0n;
  for (const input of otherAmounts) {
    if (!input.trim()) continue;
    const amount = solanaAmount(input, token);
    if (amount === null) return 0n;
    others += amount;
  }
  return maxTotal > others ? maxTotal - others : 0n;
}
